import { FiUser, FiUsers, FiCpu, FiGrid } from "react-icons/fi";
import { cn } from "utils/helpers";
import type { PanelMode } from "../../../types/panel.type";
import { WarningBox } from "newui/components/IconBox";

interface PanelModeOption {
	description: string;
	icon: React.ReactNode;
	label: string;
	mode: PanelMode;
	requiresAI?: boolean;
}

const MODE_OPTIONS: PanelModeOption[] = [
	{
		mode: "manual_panel",
		label: "Invite Panel",
		description: "Invite colleagues and external experts to score submissions",
		icon: <FiUsers size={24} />,
	},
	{
		mode: "ai_solo",
		label: "AI Evaluation",
		description: "Use AI to automatically evaluate all submissions",
		icon: <FiCpu size={24} />,
		requiresAI: true,
	},
	{
		mode: "ai_panel",
		label: "AI Panel",
		description: "Combine AI personas with human panel members",
		icon: <FiGrid size={24} />,
		requiresAI: true,
	},
];

interface PanelModeSelectorProps {
	disabled?: boolean;
	hasAIAccess: boolean;
	isRfxLead: boolean;
	selectedMode: PanelMode;
	onSelectMode: (mode: PanelMode) => void;
}

export const PanelModeSelector: React.FC<PanelModeSelectorProps> = ({
	selectedMode,
	hasAIAccess,
	isRfxLead,
	onSelectMode,
	disabled = false,
}) => {
	const visibleOptions = MODE_OPTIONS.filter(
		(option) => (!option.requiresAI || hasAIAccess) && (isRfxLead || option.mode === selectedMode)
	);

	return (
		<div className="mb-6">
			{disabled && isRfxLead && (
				<WarningBox className="mb-4">
					Evaluation mode cannot be changed after invitations have been sent.
				</WarningBox>
			)}
			{isRfxLead && <h3 className="text-sm font-medium text-gray-700 mb-3">
				How would you like to evaluate?
			</h3>}
			<div
				className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
				role="radiogroup"
				aria-label="Evaluation mode"
			>
				{visibleOptions.map((option) => {
					const isSelected = selectedMode === option.mode;
					return (
						<button
							key={option.mode}
							type="button"
							role="radio"
							aria-checked={isSelected}
							onClick={() => {
								if (!disabled) {
									onSelectMode(option.mode);
								}
							}}
							disabled={disabled}
							data-testid={`panel-mode-${option.mode}`}
							className={cn(
								"flex flex-col items-center rounded-lg border-2 p-4 text-center transition-colors",
								isSelected
									? "border-[#0031DD] bg-[#e8eeff]"
									: disabled
										? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
										: "border-gray-200 bg-white hover:border-[#0031DD]/40 hover:bg-gray-50"
							)}
						>
							<div
								className={cn(
									"mb-2",
									isSelected ? "text-[#0031DD]" : "text-gray-400"
								)}
							>
								{option.icon}
							</div>
							<span
								className={cn(
									"text-sm font-semibold",
									isSelected ? "text-[#0031DD]" : "text-gray-900"
								)}
							>
								{option.label}
							</span>
							<span className="mt-1 text-xs text-gray-500">
								{option.description}
							</span>
						</button>
					);
				})}
			</div>
		</div>
	);
};
